<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings' }">
                        <font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <h1 class="mt-1">
                        {{ $t("mainmenu.settings") }} /
                        {{ $t("settings.account.My account") }}
                    </h1>

                    <hr />

                    <div v-if="isLoading" class="d-flex justify-content-center mb-3">
                        <b-spinner />
                    </div>

                    <b-alert :show="userData === undefined" variant="danger">
                        <div class="alert-body">
                            <font-awesome-icon class="mr-50" icon="triangle-exclamation" size="lg" />
                            {{ $t("Record not found") }}
                        </div>
                    </b-alert>

                    <validation-observer v-if="userData" ref="validationRules" name="user" tag="form">
                        <profile :user-data="userData" />
                        <preferences :user-data="userData" />
                        <notifications :user-data="userData" />
                    </validation-observer>

                    <b-form-group
                        v-if="!isLoading"
                        id="account-save"
                        content-cols-lg="7"
                        content-cols-sm
                        label=""
                        label-cols-lg="3"
                        label-cols-sm="4"
                    >
                        <b-button
                            :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                            :disabled="isSaving"
                            class="d-flex justify-content-center mb-1"
                            variant="primary"
                            @click="submit()"
                        >
                            <b-spinner v-if="isSaving" class="mr-50" small></b-spinner>
                            {{ $t("Save") }}
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import {
    BAlert,
    BButton,
    BCol,
    BContainer,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BFormInput,
    BLink,
    BPagination,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
} from "bootstrap-vue";
import { onMounted, ref } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import { getUserData } from "@/auth/getUserData";
import axios from "@/libs/axios";
import * as lodash from "lodash";

import Profile from "./components/profile";
import Preferences from "./components/preferences";
import Notifications from "./components/notifications";
import useWebNotifications from "@/composables/useWebNotifications";
import useAppConfig from "@/composables/useAppConfig";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import useUserSettings from "@/composables/useUserSettings";
import useSoundNotifications from "@/composables/useSoundNotifications";

export default {
    components: {
        BContainer,
        BRow,
        BCol,
        BFormInput,
        BFormGroup,
        BButton,
        BTable,
        BDropdown,
        BDropdownItem,
        BAlert,
        BPagination,
        BTabs,
        BTab,
        BLink,
        BSpinner,
        ValidationProvider,
        ValidationObserver,
        Profile,
        Preferences,
        Notifications,
    },
    data() {
        return {
            required,
        };
    },
    setup() {
        const toast = useToast();
        const userData = ref(null);
        const isSaving = false;
        const isLoading = ref(true);
        let deferredPrompt;

        const promptInstall = () => {
            // Show the prompt:
            deferredPrompt.prompt();

            // Wait for the user to respond to the prompt:
            deferredPrompt.userChoice.then(() => (deferredPrompt = null));
        };

        onMounted(() => {
            window.addEventListener("beforeinstallprompt", function (e) {
                e.preventDefault(); // Prevent Chrome 67 and earlier from automatically showing the prompt:
                deferredPrompt = e;
            });
        });

        axios
            .get(`users/${getUserData().id}`, {
                params: {
                    fields: "name,displayName,email,pictureUrl",
                },
            })
            .then((response) => (userData.value = response.data))
            .catch(() => (userData.value = undefined))
            .finally(() => (isLoading.value = false));

        const {
            notifyIncomingMessage,
            notifyIncomingMessageNew,
            notifyConversationUnassigned,
            notifyConversationAssigned,
            notifyTeamchatStarted,
            notifyTeamchatMessage,
        } = useWebNotifications();
        const {
            soundNotifyIncomingMessage,
            soundNotifyIncomingMessageNew,
            soundNotifyConversationUnassigned,
            soundNotifyConversationAssigned,
            soundNotifyTeamchatStarted,
            soundNotifyTeamchatMessage,
        } = useSoundNotifications();
        const { smartInbox, timeNotation, language, enterToSend } = useAppConfig();
        const { setProfilePicture } = useUserSettings();

        return {
            userData,
            isSaving,
            isLoading,
            // Singular settings for local responsiveness
            notifyIncomingMessage,
            notifyIncomingMessageNew,
            notifyConversationUnassigned,
            notifyConversationAssigned,
            notifyTeamchatStarted,
            notifyTeamchatMessage,
            soundNotifyIncomingMessage,
            soundNotifyIncomingMessageNew,
            soundNotifyConversationUnassigned,
            soundNotifyConversationAssigned,
            soundNotifyTeamchatStarted,
            soundNotifyTeamchatMessage,
            smartInbox,
            timeNotation,
            language,
            enterToSend,
            toast,
            promptInstall,
            deferredPrompt,
            setProfilePicture,
        };
    },
    methods: {
        submit() {
            this.isSaving = true;
            return new Promise((resolve, reject) => {
                return this.$refs.validationRules.validate().then((success) => {
                    if (success) {
                        this.userData.settings = this.userData.settings?.filter(
                            (setting) => setting.name !== "enforceTeamFilter"
                        );
                        const localUser = getUserData();
                        return axios
                            .patch(`users/${localUser.id}`, this.userData)
                            .then((updatedUser) => {
                                // Update the local userdata with the data that has been saved (id's for newly created settings to prevent errors)
                                this.userData.settings = updatedUser.data.settings;
                                localUser.settings = updatedUser.data.settings;

                                this.toast.success(i18n.t("settings.account.update.success"));

                                localUser.displayName = this.userData.displayName;
                                localStorage.setItem("userData", JSON.stringify(localUser));
                                this.setProfilePicture(this.userData.pictureUrl);

                                const settingsObject = lodash.keyBy(this.userData.settings, (setting) => {
                                    return setting.name;
                                });

                                // Also set the new settings values locally
                                this.notifyIncomingMessage = settingsObject.notifyIncomingMessage.value;
                                this.notifyIncomingMessageNew = settingsObject.notifyIncomingMessageNew.value;
                                this.notifyConversationUnassigned = settingsObject.notifyConversationUnassigned.value;
                                this.notifyConversationAssigned = settingsObject.notifyConversationAssigned.value;
                                this.notifyTeamchatStarted = settingsObject.notifyTeamchatStarted.value;
                                this.notifyTeamchatMessage = settingsObject.notifyTeamchatMessage.value;
                                this.soundNotifyIncomingMessage = settingsObject.soundNotifyIncomingMessage.value;
                                this.soundNotifyIncomingMessageNew = settingsObject.soundNotifyIncomingMessageNew.value;
                                this.soundNotifyConversationUnassigned =
                                    settingsObject.soundNotifyConversationUnassigned.value;
                                this.soundNotifyConversationAssigned =
                                    settingsObject.soundNotifyConversationAssigned.value;
                                this.soundNotifyTeamchatStarted = settingsObject.soundNotifyTeamchatStarted.value;
                                this.soundNotifyTeamchatMessage = settingsObject.soundNotifyTeamchatMessage.value;
                                this.smartInbox = settingsObject.smartInboxEnabled.value;
                                this.timeNotation = settingsObject.timeNotation.value;
                                this.language = settingsObject.language.value;
                                this.enterToSend = settingsObject.enterToSend.value;

                                resolve(true);
                            })
                            .catch(() => {
                                this.toast.error(i18n.t("settings.account.update.error"));
                            });
                    } else {
                        reject();
                    }
                });
            })
                .catch(() => {
                    this.toast.error(i18n.t("settings.account.update.error"));
                })
                .finally(() => (this.isSaving = false));
        },
    },
};
</script>
